import { useCallback, useMemo, useState } from 'react';

import {
  isRouteActive,
  NAVIGATION_GROUPS,
  NAVIGATION_ITEMS,
  type NavigationGroup,
  type NavigationModule,
} from '@app/navigation/registry';
import { ChevronDown, ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { type ShortcutInput, useRegisterShortcuts } from '@erp-core/shortcuts';

import { useAuthStore } from '@stores/auth-store';
import { useUIStore } from '@stores/ui-store';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shared/components/ui/tooltip';
import { cn } from '@shared/utils/cn';

import styles from './ERPSidebar.module.scss';

function hasShortcut(
  item: NavigationModule,
): item is NavigationModule & { readonly shortcut: string } {
  return typeof item.shortcut === 'string';
}

function shortcutKey(shortcut: string): string {
  return shortcut.slice(shortcut.lastIndexOf('+') + 1).toLowerCase();
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ERPSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const closeMobileMenu = useUIStore((s) => s.closeMobileMenu);
  const mobileOpen = useUIStore((s) => s.mobileMenuOpen);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  // Track which groups are expanded (default: all expanded)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(NAVIGATION_GROUPS.map((g) => [g.id, true])),
  );

  const toggleGroup = useCallback(
    (groupId: string) => {
      if (collapsed) return; // Don't toggle groups when sidebar is collapsed
      setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
    },
    [collapsed],
  );

  // Register keyboard shortcuts for module navigation
  const shortcutInputs = useMemo<ShortcutInput[]>(
    () =>
      NAVIGATION_ITEMS.filter(hasShortcut).map((item) => ({
        id: `nav-${item.id}`,
        combo: {
          key: shortcutKey(item.shortcut),
          modifiers: ['alt'],
        },
        scope: 'global',
        handler: () => {
          navigate(item.path);
          closeMobileMenu();
        },
        meta: { label: item.label, group: 'Navigation', visible: true },
      })),
    [closeMobileMenu, navigate],
  );

  useRegisterShortcuts(shortcutInputs, [closeMobileMenu, navigate]);

  // ── Render helpers ──────────────────────────────────────────────────────────

  function renderNavItem(item: NavigationModule) {
    const Icon = item.icon;
    const active = isRouteActive(location.pathname, item.path);

    // ── The nav item DOM structure is IDENTICAL in both states ──────────────
    // We never add/remove children based on `collapsed`. Instead, the text
    // wrapper (.navTextGroup) collapses via CSS (max-width → 0) so the icon
    // container (.navIcon) stays at the exact same X/Y position always.
    const inner = (
      <NavLink
        to={item.path}
        className={({ isActive }) =>
          cn(styles.navItem, (isActive || active) && styles.navItemActive)
        }
        onClick={closeMobileMenu}
        aria-current={active ? 'page' : undefined}
      >
        {/* Icon — stable anchor, position never changes */}
        <span className={styles.navIcon} aria-hidden="true">
          <Icon size={collapsed ? 26 : 22} />
        </span>

        {/* Text group — collapses horizontally in collapsed mode */}
        <span
          className={cn(styles.navTextGroup, collapsed && styles.navTextGroupHidden)}
          aria-hidden={collapsed}
        >
          <span className={styles.navLabel}>{item.label}</span>
          {item.shortcut && <span className={styles.navShortcut}>{item.shortcut}</span>}
        </span>
      </NavLink>
    );

    // Wrap in tooltip when collapsed for accessibility
    if (collapsed) {
      return (
        <TooltipProvider key={item.id} delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>{inner}</TooltipTrigger>
            <TooltipContent
              side="right"
              sideOffset={10}
              showArrow
              className={styles.sidebarTooltip}
            >
              <span>{item.label}</span>
              {item.shortcut && <kbd className={styles.tooltipKbd}>{item.shortcut}</kbd>}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return <div key={item.id}>{inner}</div>;
  }

  function renderGroup(group: NavigationGroup) {
    const isExpanded = expandedGroups[group.id] ?? true;

    // Filter items by permission — hide items the user cannot access
    const visibleItems = group.items.filter(
      (item) => !item.permission || hasPermission(item.permission),
    );

    // Hide the entire group if it has no visible items
    if (visibleItems.length === 0) return null;

    return (
      <div key={group.id} className={styles.group}>
        {/* Group header — collapses to height:0 in collapsed mode instead of
            being removed from the DOM, so the items below don't shift. */}
        <button
          type="button"
          className={cn(styles.groupHeader, collapsed && styles.groupHeaderHidden)}
          onClick={() => toggleGroup(group.id)}
          aria-expanded={collapsed ? undefined : isExpanded}
          tabIndex={collapsed ? -1 : 0}
        >
          <span className={styles.groupLabel}>{group.label}</span>
          <span className={styles.groupChevron} aria-hidden="true">
            {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </span>
        </button>

        {(collapsed || isExpanded) && (
          <div className={styles.groupItems} role="list" aria-label={group.label}>
            {visibleItems.map((item) => (
              <div key={item.id} role="listitem">
                {renderNavItem(item)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div className={styles.mobileBackdrop} onClick={closeMobileMenu} aria-hidden="true" />
      )}

      <aside
        className={cn(
          styles.sidebar,
          collapsed && styles.sidebarCollapsed,
          mobileOpen && styles.sidebarMobileOpen,
        )}
        role="navigation"
        aria-label="Main navigation"
        id="erp-sidebar"
      >
        {/* ── Brand / Logo ────────────────────────────────────────────────── */}
        <div className={styles.brand} aria-label="GarageWale ERP">
          <div className={styles.brandIcon} aria-hidden="true">
            <img
              src="/images/garagewalelogo.jpg"
              alt="GarageWale Logo"
              className={styles.brandIconImg}
            />
          </div>
          {/* Brand text stays in DOM — collapses via CSS so logo never shifts */}
          <div
            className={cn(styles.brandText, collapsed && styles.brandTextHidden)}
            aria-hidden={collapsed}
          >
            <span className={styles.brandName}>GarageWale</span>
            <span className={styles.brandTagline}>Workshop ERP</span>
          </div>
        </div>

        {/* ── Navigation groups ────────────────────────────────────────────── */}
        <nav className={styles.nav} aria-label="Module navigation">
          {NAVIGATION_GROUPS.map(renderGroup)}
        </nav>

        {/* ── Bottom Section (Help & Support + Collapse) ───────────────────── */}
        <div className={styles.bottomSection}>
          {/* Help & Support */}
          {/* {collapsed ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className={styles.helpBtn} aria-label="Help & Support">
                    <Headphones size={20} aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  sideOffset={10}
                  showArrow
                  className={styles.sidebarTooltip}
                >
                  <span>Help & Support</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <button type="button" className={styles.helpBtn}>
              <Headphones size={20} aria-hidden="true" />
              <span>Help & Support</span>
            </button>
          )} */}

          {/* Collapse Toggle — same structure in both states; label collapses via CSS */}
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className={styles.collapseBtn}
                  onClick={toggleSidebar}
                  aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  {collapsed ? (
                    <PanelLeftOpen size={18} aria-hidden="true" />
                  ) : (
                    <PanelLeftClose size={18} aria-hidden="true" />
                  )}
                  <span
                    className={cn(
                      styles.collapseBtnLabel,
                      collapsed && styles.collapseBtnLabelHidden,
                    )}
                    aria-hidden={collapsed}
                  >
                    Collapse
                  </span>
                </button>
              </TooltipTrigger>
              {collapsed && (
                <TooltipContent
                  side="right"
                  sideOffset={10}
                  showArrow
                  className={styles.sidebarTooltip}
                >
                  Expand sidebar (Ctrl+B)
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </aside>
    </>
  );
}
